import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, IconButton, Button as MuiButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  showCloseButton?: boolean;
}

/**
 * Modal moderne avec animations
 */
export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = 'sm',
  showCloseButton = true,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
        },
      }}
      TransitionProps={{
        timeout: 300,
      }}
    >
      {showCloseButton && (
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'text.secondary',
            '&:hover': {
              background: 'rgba(18, 155, 175, 0.1)',
              color: '#129baf',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      )}

      <DialogTitle sx={{ pb: 2, pt: 3 }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 700, color: '#00355f' }}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        <Box sx={{ mt: 1 }}>{children}</Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        {actions}
      </DialogActions>
    </Dialog>
  );
};

/**
 * Modal de confirmation
 */
interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  severity?: 'info' | 'warning' | 'error' | 'success';
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmer',
  cancelText = 'Annuler',
  severity = 'warning',
}) => {
  const getSeverityColor = () => {
    switch (severity) {
      case 'error':
        return '#e74c3c';
      case 'success':
        return '#2ecc71';
      case 'warning':
        return '#f39c12';
      default:
        return '#129baf';
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      maxWidth="sm"
      actions={
        <>
          <MuiButton
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: 3,
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            {cancelText}
          </MuiButton>
          <MuiButton
            onClick={onConfirm}
            variant="contained"
            sx={{
              borderRadius: 3,
              fontWeight: 600,
              textTransform: 'none',
              background: `linear-gradient(135deg, ${getSeverityColor()} 0%, ${getSeverityColor()}dd 100%)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${getSeverityColor()}dd 0%, ${getSeverityColor()}cc 100%)`,
              },
            }}
          >
            {confirmText}
          </MuiButton>
        </>
      }
    >
      <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
        {message}
      </Typography>
    </Modal>
  );
};

export default Modal;
